import { type ChartworkTechnique } from "@nav-games/chartwork";

interface ChartworkTechniqueFigureProps {
  technique: ChartworkTechnique;
  size?: number;
}

/// Full RYA-style construction diagram for each chartwork
/// technique: Dead Reckoning, Estimated Position, Course To Steer.
/// One renderer per id (dispatched by `technique.id`) — the
/// diagrams are too distinct to fit a generic template.
///
/// Conventions match the catalogue:
///   - Orange = DR
///   - Magenta = EP
///   - Green = CTS
///   - Black = fix dot, ground / water / tidal annotations
export function ChartworkTechniqueFigure({
  technique,
  size = 360,
}: ChartworkTechniqueFigureProps) {
  return (
    <svg
      viewBox="0 0 400 280"
      width={size}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      role="img"
      aria-label={`${technique.name} construction diagram`}
    >
      <rect width="400" height="280" fill="#fdfbf2" />
      {technique.id === "dr" && <DrDiagram />}
      {technique.id === "ep" && <EpDiagram />}
      {technique.id === "cts" && <CtsDiagram />}
    </svg>
  );
}

// ─── Colours ────────────────────────────────────────────────────

const ORANGE = "#e08020";
const MAGENTA = "#d000a0";
const GREEN = "#3aa055";
const BLACK = "#1c2530";
const MUTED = "#7a6f56";

// ─── DR ─────────────────────────────────────────────────────────
//
// Construction:
//   Fix at A (top-left) → water-track line out to the right with
//   a single chevron → DR marked by perpendicular tick after the
//   log distance run.

function DrDiagram() {
  const A = { x: 60, y: 80 };
  const B = { x: 320, y: 160 }; // DR position along the heading line
  return (
    <g fontFamily="ui-sans-serif, system-ui">
      {/* Fix at A */}
      <FixMark x={A.x} y={A.y} time="10:00" />

      {/* Water track from A to B */}
      <line
        x1={A.x}
        y1={A.y}
        x2={B.x}
        y2={B.y}
        stroke={ORANGE}
        strokeWidth={2}
      />
      <ChevronOnLine
        from={A}
        to={B}
        t={0.45}
        count={1}
        colour={ORANGE}
      />

      {/* DR tick at B, perpendicular to the track */}
      <DrTick x={B.x} y={B.y} fromX={A.x} fromY={A.y} colour={ORANGE} />
      <text
        x={B.x + 12}
        y={B.y + 6}
        fontSize="13"
        fontWeight="700"
        fill={ORANGE}
      >
        DR · 11:00
      </text>
      <text x={B.x + 12} y={B.y + 22} fontSize="11" fill={MUTED}>
        log 4.2 nm
      </text>

      {/* Legend block bottom-left */}
      <text x={20} y={250} fontSize="11" fill={MUTED}>
        Course steered + log distance · no tide / leeway applied
      </text>
    </g>
  );
}

// ─── EP ─────────────────────────────────────────────────────────
//
// Construction:
//   Fix at A → DR at B (orange water track) → tidal vector from B
//   to C (triple chevron, black) → EP at C (magenta triangle).
//   The ground track is the line from A to C (double chevron).

function EpDiagram() {
  const A = { x: 50, y: 70 };
  const DR = { x: 280, y: 140 };
  const EP = { x: 320, y: 200 }; // DR + tidal vector
  return (
    <g fontFamily="ui-sans-serif, system-ui">
      {/* Fix at A */}
      <FixMark x={A.x} y={A.y} time="10:00" />

      {/* Water track A → DR */}
      <line x1={A.x} y1={A.y} x2={DR.x} y2={DR.y} stroke={ORANGE} strokeWidth={2} />
      <ChevronOnLine from={A} to={DR} t={0.45} count={1} colour={ORANGE} />
      <DrTick x={DR.x} y={DR.y} fromX={A.x} fromY={A.y} colour={ORANGE} />
      <text
        x={DR.x - 14}
        y={DR.y - 10}
        fontSize="12"
        fontWeight="700"
        fill={ORANGE}
        textAnchor="end"
      >
        DR
      </text>

      {/* Tidal vector DR → EP (triple chevron) */}
      <line
        x1={DR.x}
        y1={DR.y}
        x2={EP.x}
        y2={EP.y}
        stroke={BLACK}
        strokeWidth={2}
      />
      <ChevronOnLine from={DR} to={EP} t={0.55} count={3} colour={BLACK} />

      {/* Ground track A → EP (double chevron) */}
      <line
        x1={A.x}
        y1={A.y}
        x2={EP.x}
        y2={EP.y}
        stroke={MAGENTA}
        strokeWidth={1.5}
        strokeDasharray="6 5"
      />
      <ChevronOnLine
        from={A}
        to={EP}
        t={0.7}
        count={2}
        colour={MAGENTA}
      />

      {/* EP marker — triangle around dot */}
      <EpMark x={EP.x} y={EP.y} time="11:00" />

      {/* Labels */}
      <text x={20} y={245} fontSize="11" fill={MUTED}>
        DR + tidal vector for the hour = EP. Dashed magenta = ground track.
      </text>
    </g>
  );
}

// ─── CTS — the classic tidal triangle ─────────────────────────
//
// Construction:
//   A (start) → ground track to B (destination).
//   From A, draw tidal vector A→C for the period (triple chevron).
//   With dividers set to boat-speed × time, swing from C to
//   intersect A→B at D. C→D is the water track (single chevron),
//   its bearing is the CTS.

function CtsDiagram() {
  const A = { x: 50, y: 200 };
  const B = { x: 350, y: 80 }; // destination, ground track endpoint
  const C = { x: 130, y: 230 }; // end of tidal vector (set + drift × time)
  const D = { x: 240, y: 132 }; // boat will arrive here in `t` hours
  return (
    <g fontFamily="ui-sans-serif, system-ui">
      {/* Desired ground track A → B (full length, dashed for reference) */}
      <line
        x1={A.x}
        y1={A.y}
        x2={B.x}
        y2={B.y}
        stroke={MUTED}
        strokeWidth={1.3}
        strokeDasharray="3 4"
      />
      <text
        x={B.x + 8}
        y={B.y - 4}
        fontSize="12"
        fontWeight="700"
        fill={BLACK}
      >
        B (destination)
      </text>

      {/* Ground track A → D (solid section, the part travelled in `t`) */}
      <line
        x1={A.x}
        y1={A.y}
        x2={D.x}
        y2={D.y}
        stroke={BLACK}
        strokeWidth={2}
      />
      <ChevronOnLine from={A} to={D} t={0.6} count={2} colour={BLACK} />

      {/* Tidal vector A → C (triple chevron) */}
      <line x1={A.x} y1={A.y} x2={C.x} y2={C.y} stroke={BLACK} strokeWidth={2} />
      <ChevronOnLine from={A} to={C} t={0.55} count={3} colour={BLACK} />
      <text x={C.x + 8} y={C.y + 14} fontSize="12" fill={BLACK} fontWeight="700">
        C
      </text>
      <text x={C.x + 8} y={C.y + 28} fontSize="10" fill={MUTED}>
        set + drift × time
      </text>

      {/* Water track / CTS C → D (single chevron, GREEN) */}
      <line x1={C.x} y1={C.y} x2={D.x} y2={D.y} stroke={GREEN} strokeWidth={2.5} />
      <ChevronOnLine from={C} to={D} t={0.5} count={1} colour={GREEN} />
      <text
        x={(C.x + D.x) / 2 + 8}
        y={(C.y + D.y) / 2 + 4}
        fontSize="12"
        fontWeight="700"
        fill={GREEN}
      >
        CTS
      </text>

      {/* Arc swept from C with boat-speed radius onto A→B */}
      <path
        d={`M ${C.x + 90} ${C.y - 5} A 110 110 0 0 1 ${D.x - 6} ${D.y + 4}`}
        fill="none"
        stroke={MUTED}
        strokeWidth={1}
        strokeDasharray="2 3"
      />

      {/* Marks at A, D */}
      <FixMark x={A.x} y={A.y} time="A" />
      <circle cx={D.x} cy={D.y} r={4} fill={BLACK} />
      <text
        x={D.x + 8}
        y={D.y - 6}
        fontSize="12"
        fill={BLACK}
        fontWeight="700"
      >
        D
      </text>

      <text x={20} y={265} fontSize="11" fill={MUTED}>
        A→B = desired track. A→C = tide. C→D drawn at boat speed = CTS to steer.
      </text>
    </g>
  );
}

// ─── Mark primitives ────────────────────────────────────────────

function FixMark({ x, y, time }: { x: number; y: number; time: string }) {
  return (
    <g>
      <line x1={x - 10} y1={y} x2={x + 10} y2={y} stroke={BLACK} strokeWidth={1.5} />
      <line x1={x} y1={y - 10} x2={x} y2={y + 10} stroke={BLACK} strokeWidth={1.5} />
      <circle cx={x} cy={y} r={7} fill="none" stroke={BLACK} strokeWidth={2} />
      <circle cx={x} cy={y} r={2.5} fill={BLACK} />
      <text x={x - 15} y={y - 12} fontSize="11" fontWeight="700" fill={BLACK} textAnchor="end">
        {time}
      </text>
    </g>
  );
}

function DrTick({
  x,
  y,
  fromX,
  fromY,
  colour,
}: {
  x: number;
  y: number;
  fromX: number;
  fromY: number;
  colour: string;
}) {
  // Tick perpendicular to the segment from (fromX,fromY) → (x,y)
  const dx = x - fromX;
  const dy = y - fromY;
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular unit vector, scaled to the tick half-length.
  const tx = -dy / len;
  const ty = dx / len;
  const half = 10;
  return (
    <line
      x1={x + tx * half}
      y1={y + ty * half}
      x2={x - tx * half}
      y2={y - ty * half}
      stroke={colour}
      strokeWidth={3}
      strokeLinecap="round"
    />
  );
}

function EpMark({ x, y, time }: { x: number; y: number; time: string }) {
  return (
    <g>
      <polygon
        points={`${x},${y - 11} ${x + 11},${y + 8} ${x - 11},${y + 8}`}
        fill="none"
        stroke={MAGENTA}
        strokeWidth={2}
      />
      <circle cx={x} cy={y} r={2.5} fill={MAGENTA} />
      <text
        x={x + 14}
        y={y + 4}
        fontSize="12"
        fontWeight="700"
        fill={MAGENTA}
      >
        EP · {time}
      </text>
    </g>
  );
}

function ChevronOnLine({
  from,
  to,
  t,
  count,
  colour,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  t: number;
  count: 1 | 2 | 3;
  colour: string;
}) {
  // Position chevron(s) at parameter t along the line. Each
  // chevron points in the direction of travel (from → to). With
  // multiple chevrons they're spaced 10 viewBox units apart.
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const cx = from.x + dx * t;
  const cy = from.y + dy * t;
  const size = 6;
  const spacing = 9;
  return (
    <g>
      {Array.from({ length: count }).map((_, i) => {
        // Offset along travel direction so chevrons stack
        const offset = (i - (count - 1) / 2) * spacing;
        const ax = cx + ux * offset;
        const ay = cy + uy * offset;
        // Each chevron is a "<" shape pointing in travel direction.
        const back = -size;
        const wing = size;
        return (
          <polyline
            key={i}
            points={`
              ${ax + ux * back + px * wing},${ay + uy * back + py * wing}
              ${ax},${ay}
              ${ax + ux * back - px * wing},${ay + uy * back - py * wing}
            `}
            fill="none"
            stroke={colour}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}
